'use client';

import { useTranslations } from 'next-intl';
import {
  Eye,
  Pencil,
  Copy,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Star,
  EllipsisVertical
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { PermissionGate } from '@/features/auth';
import type { DynamicForm, DynamicFormType } from '../types';

interface DynamicFormCompactRowProps {
  form: DynamicForm;
  onPreview: (form: DynamicForm) => void;
  onEdit: (form: DynamicForm) => void;
  onDuplicate: (form: DynamicForm) => void;
  onDelete: (form: DynamicForm) => void;
  onToggleStatus: (form: DynamicForm) => void;
  onToggleDefault: (form: DynamicForm) => void;
  isDuplicating?: boolean;
  isTogglingStatus?: boolean;
  isTogglingDefault?: boolean;
}

const typeTranslationKeys: Record<DynamicFormType, string> = {
  registration: 'typeRegistration',
  filling: 'typeFilling'
};

export function DynamicFormCompactRow({
  form,
  onPreview,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleStatus,
  onToggleDefault
}: DynamicFormCompactRowProps) {
  const t = useTranslations('dynamicForms');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{form.name}</span>
          <span className='text-muted-foreground hidden text-sm sm:inline'>
            {form.form_id_mask}
          </span>
          {form.is_default && (
            <Star className='h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500' />
          )}
        </div>
      </div>

      <Badge variant='outline' className='hidden sm:inline-flex'>
        {t(typeTranslationKeys[form.type])}
      </Badge>

      <Badge
        variant='default'
        className={cn(
          form.status === 'active'
            ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
            : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
        )}
      >
        {form.status === 'active' ? t('statusActive') : t('statusDraft')}
      </Badge>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {form.fields_count ?? form.fields?.length ?? 0} {t('fields')}
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='sm' className='h-7 w-7 shrink-0 p-0'>
            <EllipsisVertical className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={() => onPreview(form)}>
            <Eye className='me-2 h-3.5 w-3.5' />
            {t('preview')}
          </DropdownMenuItem>
          <PermissionGate permissions={['dynamic_forms.edit']}>
            <DropdownMenuItem onClick={() => onEdit(form)}>
              <Pencil className='me-2 h-3.5 w-3.5' />
              {t('edit')}
            </DropdownMenuItem>
          </PermissionGate>
          <PermissionGate permissions={['dynamic_forms.create']}>
            <DropdownMenuItem onClick={() => onDuplicate(form)}>
              <Copy className='me-2 h-3.5 w-3.5' />
              {t('duplicate')}
            </DropdownMenuItem>
          </PermissionGate>
          <DropdownMenuSeparator />
          <PermissionGate permissions={['dynamic_forms.edit']}>
            <DropdownMenuItem onClick={() => onToggleStatus(form)}>
              {form.status === 'active' ? (
                <ToggleRight className='me-2 h-3.5 w-3.5' />
              ) : (
                <ToggleLeft className='me-2 h-3.5 w-3.5' />
              )}
              {form.status === 'active' ? t('deactivate') : t('activate')}
            </DropdownMenuItem>
          </PermissionGate>
          <PermissionGate permissions={['dynamic_forms.edit']}>
            <DropdownMenuItem onClick={() => onToggleDefault(form)}>
              <Star
                className={cn(
                  'me-2 h-3.5 w-3.5',
                  form.is_default && 'fill-current'
                )}
              />
              {form.is_default ? t('unsetDefault') : t('setDefault')}
            </DropdownMenuItem>
          </PermissionGate>
          <DropdownMenuSeparator />
          <PermissionGate permissions={['dynamic_forms.delete']}>
            <DropdownMenuItem
              className='text-destructive focus:text-destructive'
              onClick={() => onDelete(form)}
            >
              <Trash2 className='me-2 h-3.5 w-3.5' />
              {t('delete')}
            </DropdownMenuItem>
          </PermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
